import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_URL from "../config";

const ACOES = [
    { value: "",                   label: "Todas" },
    { value: "RELATORIO_GERADO",   label: "Relatório gerado" },
    { value: "RELATORIO_EXCLUIDO", label: "Relatório excluído" },
    { value: "SERVIDOR_EDITADO",   label: "Servidor editado" },
    { value: "SERVIDOR_EXCLUIDO",  label: "Servidor excluído" },
    { value: "USUARIO_CRIADO",     label: "Usuário criado" },
    { value: "USUARIO_EDITADO",    label: "Usuário editado" },
    { value: "USUARIO_EXCLUIDO",   label: "Usuário excluído" },
    { value: "SENHA_RESETADA",     label: "Senha resetada" },
];

const BADGE = {
    RELATORIO_GERADO:   "bg-blue-100 text-blue-700",
    RELATORIO_EXCLUIDO: "bg-orange-100 text-orange-700",
    SERVIDOR_EDITADO:   "bg-yellow-100 text-yellow-700",
    SERVIDOR_EXCLUIDO:  "bg-red-100 text-red-700",
    USUARIO_CRIADO:     "bg-green-100 text-green-700",
    USUARIO_EDITADO:    "bg-yellow-100 text-yellow-700",
    USUARIO_EXCLUIDO:   "bg-red-100 text-red-700",
    SENHA_RESETADA:     "bg-purple-100 text-purple-700",
};

const formatarData = (iso) =>
    new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

export default function Logs() {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filtroAcao, setFiltroAcao] = useState("");
    const [busca, setBusca] = useState("");
    const [carregando, setCarregando] = useState(false);
    const debounceRef = useRef(null);

    const buscar = async (p = 1, acao = filtroAcao, termo = busca) => {
        setCarregando(true);
        try {
            const params = new URLSearchParams({ page: p, limit: 10 });
            if (acao)  params.append("acao",  acao);
            if (termo) params.append("busca", termo);
            const res = await fetch(`${API_URL}/api/logs?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const json = await res.json();
            setLogs(json.data);
            setTotal(json.total);
            setPage(json.page);
            setTotalPages(json.totalPages);
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => { buscar(1); }, []);

    const handleFiltroAcao = (acao) => {
        setFiltroAcao(acao);
        buscar(1, acao, busca);
    };

    const handleBusca = (e) => {
        const termo = e.target.value;
        setBusca(termo);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => buscar(1, filtroAcao, termo), 350);
    };

    const irParaPagina = (p) => buscar(p);

    return (
        <div className="w-full max-w-4xl mx-auto flex flex-col gap-4 py-6 px-4">

            {/* Header */}
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-700 uppercase tracking-wide">Log de Atividades</h1>
                <span className="text-xs text-gray-400">{total} registro{total !== 1 ? "s" : ""}</span>
            </div>

            {/* Busca */}
            <div className="relative">
                <input
                    type="text"
                    value={busca}
                    onChange={handleBusca}
                    placeholder="Buscar por usuário ou descrição..."
                    className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                />
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
                {busca && (
                    <button
                        onClick={() => { setBusca(""); buscar(1, filtroAcao, ""); }}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm"
                    >✕</button>
                )}
            </div>

            {/* Filtro por ação */}
            <div className="flex flex-wrap gap-2">
                {ACOES.map((a) => (
                    <button
                        key={a.value}
                        onClick={() => handleFiltroAcao(a.value)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors border ${
                            filtroAcao === a.value
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                        }`}
                    >
                        {a.label}
                    </button>
                ))}
            </div>

            {/* Tabela */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {carregando ? (
                    <div className="px-6 py-12 text-center text-sm text-gray-400">Carregando...</div>
                ) : logs.length === 0 ? (
                    <div className="px-6 py-12 text-center text-sm text-gray-400">
                        {busca || filtroAcao ? "Nenhum resultado encontrado." : "Nenhuma atividade registrada."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Data/Hora</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Usuário</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Ação</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Descrição</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, i) => (
                                    <tr key={log.id} className={`border-b border-gray-50 ${i % 2 !== 0 ? "bg-gray-50" : ""}`}>
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap font-mono text-xs">
                                            {formatarData(log.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="font-medium text-gray-700">{log.usuario_nome}</span>
                                            {log.usuario_perfil && (
                                                <span className="ml-1 text-xs text-gray-400 capitalize">({log.usuario_perfil})</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE[log.acao] || "bg-gray-100 text-gray-600"}`}>
                                                {ACOES.find(a => a.value === log.acao)?.label || log.acao}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{log.descricao}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Paginação */}
            <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">
                    {total === 0 ? "0 registros" : `${(page - 1) * 10 + 1}–${Math.min(page * 10, total)} de ${total}`}
                </p>
                <div className="flex items-center gap-1">
                    <button onClick={() => irParaPagina(1)} disabled={page === 1}
                        className="px-2 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                        «
                    </button>
                    <button onClick={() => irParaPagina(page - 1)} disabled={page === 1}
                        className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                        ← Anterior
                    </button>
                    <span className="px-3 py-1.5 text-xs text-gray-500">
                        {page} / {totalPages || 1}
                    </span>
                    <button onClick={() => irParaPagina(page + 1)} disabled={page >= totalPages}
                        className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                        Próxima →
                    </button>
                    <button onClick={() => irParaPagina(totalPages)} disabled={page >= totalPages}
                        className="px-2 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                        »
                    </button>
                </div>
            </div>
        </div>
    );
}
